import StarIcon from "@mui/icons-material/Star";
import { Box, Tooltip } from "@mui/material";
import { MerchantStatusCell } from "Components/Hunter/Merchants/MerchantsList/MerchantStatusCell";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";
import { DATE_FILTER_COLUMN_MIN_SIZE } from "Hooks/useServerTable";
import { LICENSE_CODE_FILTER_OPTIONS } from "Components/Licenses/licenseUtils";
import {
  MerchantStatus,
  type LicenseCode,
  type MerchantListItemDto,
} from "Infrastructure/Api/Api";
import { formatDate } from "Utils/DateUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { appUrl } from "Utils/UrlUtils";
import {
  type MRT_ColumnDef,
  type MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import { useMerchantOwnership } from "Hooks/useMerchantOwnership";

const RESOURCES = Resources.Hunter.AllMerchants;

type Props = {
  data: Array<MerchantListItemDto>;
  isRefetching?: boolean;
  isLoading?: boolean;
  rowCount?: number;
  tableProps?: Partial<MRT_TableOptions<MerchantListItemDto>>;
};

export const AllMerchantsTable = ({
  data,
  isRefetching,
  isLoading,
  rowCount,
  tableProps,
}: Props) => {
  const { t } = useResource();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();
  const { isMerchantOwned, isLoading: isLoadingOwnership } =
    useMerchantOwnership();

  const columns = useMemo<Array<MRT_ColumnDef<MerchantListItemDto>>>(
    () => [
      {
        accessorKey: "merchantStatus",
        header: t(RESOURCES.TableHeaders.Status),
        Cell: ({ row }) => (
          <MerchantStatusCell status={row.original.merchantStatus} />
        ),
        filterVariant: "multi-select",
        filterSelectOptions: [
          {
            label: t(Resources.Merchant.MerchantStatus.InDraft),
            value: MerchantStatus.InDraft,
          },
          {
            label: t(
              Resources.Merchant.MerchantStatus.InvitedWaitingForConfirmation,
            ),
            value: MerchantStatus.InvitedWaitingForConfirmation,
          },
          {
            label: t(Resources.Merchant.MerchantStatus.Onboarding),
            value: MerchantStatus.Onboarding,
          },
          {
            label: t(
              Resources.Merchant.MerchantStatus.WaitingForPaymentTerminal,
            ),
            value: MerchantStatus.WaitingForPaymentTerminal,
          },
          {
            label: t(Resources.Merchant.MerchantStatus.Active),
            value: MerchantStatus.Active,
          },
          {
            label: t(
              Resources.Merchant.MerchantStatus.BlockedFinancialServices,
            ),
            value: MerchantStatus.BlockedFinancialServices,
          },
        ],
        size: 0,
      },
      {
        accessorKey: "party.companyNumber",
        accessorFn: (row) => row.party?.companyNumber ?? "",
        header: t(RESOURCES.TableHeaders.CompanyNumber),
        size: 70,
      },
      {
        accessorKey: "party.companyName",
        accessorFn: (row) => row.party?.companyName ?? "",
        header: t(RESOURCES.TableHeaders.CompanyName),
        size: 200,
        Cell: ({ row }) => {
          const isOwned = isMerchantOwned(row.original.license?.publicID);
          const companyName = row.original.party?.companyName || "-";

          return (
            <Box display="flex" alignItems="center" gap={0.5}>
              {isOwned && (
                <Tooltip title={t(RESOURCES.OwnedByMe)} arrow>
                  <StarIcon sx={{ color: "primary.main", fontSize: 16 }} />
                </Tooltip>
              )}
              {companyName}
            </Box>
          );
        },
      },
      {
        accessorKey: "license.companyName",
        accessorFn: (row) => row.license?.companyName ?? "",
        header: t(RESOURCES.TableHeaders.LicenseOwner),
        size: 200,
      },
      {
        accessorKey: "license.code",
        accessorFn: (row) => row.license?.code ?? "",
        header: t(RESOURCES.TableHeaders.LicenseType),
        size: 130,
        Cell: ({ cell }) => {
          const code = cell.getValue<LicenseCode>();
          return code || "-";
        },
        filterVariant: "multi-select",
        filterSelectOptions: LICENSE_CODE_FILTER_OPTIONS,
      },
      {
        accessorKey: "dateCreated",
        header: t(RESOURCES.TableHeaders.DateCreated),
        size: DATE_FILTER_COLUMN_MIN_SIZE,
        filterVariant: "date",
        Cell: ({ cell }) => formatDate(cell.getValue<Date>()),
      },
      {
        accessorKey: "transactionsCount",
        header: t(RESOURCES.TableHeaders.TransactionsCount),
        size: 150,
      },
    ],
    [isMerchantOwned, t],
  );

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns,
    rowCount,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    localization,
    state: {
      ...tableProps?.state,
      isLoading: isLoading || isLoadingOwnership,
      showProgressBars: isRefetching,
    },
    initialState: {
      ...tableProps?.initialState,
      density: "compact",
    },
    muiTableBodyRowProps: ({ row }) => {
      const isOwned = isMerchantOwned(row.original.license?.publicID);
      const url = isOwned
        ? `${appUrl("merchant-detail")}/${row.original.publicID}`
        : `${appUrl("shared-merchant-detail")}/${row.original.publicID}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: { cursor: "pointer" },
      };
    },
    muiTablePaperProps: {
      sx: { borderRadius: "10px" },
    },
  });

  return <MaterialReactTable table={table} />;
};
